import * as R from 'ramda';
import { ActionType } from '../../../core/redux/actionType';
import { makeApiAction, makeRsaaTypes, ApiReducer, makeApiReducer } from '../../../core/api/rsaa';
import { Endpoints } from '../../../core/api/endpoints';
import { getLoading, addLoading, addError, noLoadingNoError } from '../../../core/api/module';
import { SendTo, Rater } from './models';
import { isNilOrEmpty } from '../../../language/module';
import { toMap } from '../../../language/arrays';
import { updateInvitedDateFor, updateInvitedCountFor, ratersByLevel } from './module';


const KEY_ACTION = ActionType.SendInvitationsRequest;
const ACTIONS    = [
  ActionType.SendInvitationsRequest,
  ActionType.SendInvitationsResponse,
  ActionType.SendInvitationsError,
];


type Payload = {
  message: string;
  sentTo: SendTo[];
};


type Meta = {
  projectId: number;
};


/**
 * Dispatch this action to send invitation emails to all as-yet uninvited raters who have been added to the given
 * project for the listed participant
 *
 * @param projectId - the project to which we wish to invite raters
 * @param participantId - the specific project participant for whom we wish to invite raters
 * @param comment - a custom comment included in the email sent out to invited raters
 * @param to - the list of email addresses to invite (along with the language that should be used for each message)
 */
export const sendInvitationsAction = (
  projectId: number,
  participantId: number,
  comment: string,
  to: SendTo[],
) => makeApiAction<Payload, Meta>(
  {
    method:   'POST',
    endpoint: Endpoints.SendInvitations,
    headers:  { 'content-type': 'application/json' },
    body:     JSON.stringify({ projectId: projectId, participantId: participantId, comment: comment, to: to }),
    types:    makeRsaaTypes<Payload, Meta>(ACTIONS, { projectId: projectId }),
    bailout:  state => !!getLoading(state, KEY_ACTION, `${projectId}`),
  },
);

const reducer: ApiReducer<Payload, Meta> = (state, action, errorMsg, isLoading, payload) => {
  const projIdString = `${action.meta.projectId}`;

  if (isLoading) {
    return addLoading(state, KEY_ACTION, projIdString);
  }

  if (errorMsg) {
    return addError(state, KEY_ACTION, errorMsg, projIdString);
  }

  if (isNilOrEmpty(payload?.sentTo)) {
    return addError(state, KEY_ACTION, payload?.message || '', projIdString);
  }

  let successState = noLoadingNoError(state, KEY_ACTION, projIdString);

  const currentRaters = Object.values(successState.projectRaters?.[projIdString] || {});
  const requirements  = state.participantRequirements ?? [];

  const invitedEmailAddresses = R.map(st => st.toAddress, payload?.sentTo ?? []);
  const hasBeenInvited        = (rater: Rater) => invitedEmailAddresses?.indexOf(rater.email) > -1;

  // Bifurcate raters based on whether they were included in the last round of invite emails
  const { ratersOnTheInvite, ratersNotOnTheInvite } = R.groupBy(
    rater => hasBeenInvited(rater) ? 'ratersOnTheInvite' : 'ratersNotOnTheInvite',
    currentRaters,
  );

  // Update raters who were included to reflect that they were invited today
  const ratersMarkedInvited = (ratersOnTheInvite ?? []).map(rater => updateInvitedDateFor(rater, new Date()));

  successState = R.assoc(
    'participantRequirements',
    requirements.map(req => updateInvitedCountFor(req, ratersByLevel(ratersMarkedInvited))),
    successState,
  );

  successState = R.dissoc('lastInviteRequest', successState);

  return R.assocPath(
    ['projectRaters', projIdString],
    toMap(ratersMarkedInvited.concat(ratersNotOnTheInvite ?? []), rater => `${rater.raterId}`),
    successState,
  );
};

export const sendInvitationsReducer = makeApiReducer<Payload, Meta>(ACTIONS, KEY_ACTION, reducer);
